'use client';

import React, { useCallback, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@west-tennessee-consulting/ui/components/tooltip';
import type { ChatItemProps } from '../types.js';
import { useChatOrganization } from '../hooks/useChatOrganization.js';
import { useUpdateChatMutation } from '../../../hooks/api/index.js';
import { useChatActivityOptional } from '../../../contexts/ChatActivityContext.js';
import { ChatItemMenu } from './ChatItemMenu.js';
import { ChatDetailsModal } from './ChatDetailsModal.js';

/**
 * A single chat row — one line, title only. Hover reveals a kebab (and, via the
 * tooltip, the conversation's meta). Right-click opens the same menu. Renaming
 * happens inline; everything else (star, move-to-project, delete, details) is in
 * the menu. Star + project grouping are backed by the interim client store.
 */
export const ChatItem: React.FC<ChatItemProps> = React.memo(
  ({
    chat,
    isSelected,
    onSelectChat,
    onRestoreChat,
    onDeleteChat,
    truncateTitle,
    formatDate,
    compactMode = false,
  }) => {
    const isArchived = chat.status === 'archived';
    // Live run state for THIS chat. Present whether or not the chat is open:
    // a turn left running in one chat stays visible while the user works in
    // another. Absent in hosts that mount no activity provider.
    const activity = useChatActivityOptional();
    const isRunning = activity?.isChatStreaming(chat.id) ?? false;
    const isUnread = !isRunning && (activity?.hasUnread(chat.id) ?? false);
    const org = useChatOrganization();
    const starred = org.isStarred(chat.id);
    const currentProjectId = org.projectIdOf(chat.id);
    const updateChat = useUpdateChatMutation();

    const [menuOpen, setMenuOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [renaming, setRenaming] = useState(false);
    const [renameValue, setRenameValue] = useState(chat.title);
    const renameInputRef = useRef<HTMLInputElement>(null);

    const startRename = useCallback(() => {
      setRenameValue(chat.title);
      setRenaming(true);
      // Focus after the input mounts.
      requestAnimationFrame(() => {
        renameInputRef.current?.focus();
        renameInputRef.current?.select();
      });
    }, [chat.title]);

    const commitRename = useCallback(() => {
      const next = renameValue.trim();
      setRenaming(false);
      if (next && next !== chat.title) {
        updateChat.mutate({ chatId: chat.id, title: next });
      }
    }, [renameValue, chat.title, chat.id, updateChat]);

    const cancelRename = useCallback(() => {
      setRenaming(false);
      setRenameValue(chat.title);
    }, [chat.title]);

    const handleContextMenu = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      setMenuOpen(true);
    }, []);

    const projectName = currentProjectId
      ? org.projects.find((p) => p.id === currentProjectId)?.name ?? null
      : null;

    const rowPad = compactMode ? 'px-2 py-1.5' : 'px-2.5 py-2';
    const textSize = compactMode ? 'text-xs' : 'text-sm';

    return (
      <>
        <div
          onClick={() => !renaming && onSelectChat(chat.id)}
          onContextMenu={handleContextMenu}
          onKeyDown={(e) => {
            if (renaming) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectChat(chat.id);
            }
          }}
          role="button"
          tabIndex={0}
          className={`group relative flex items-center gap-2 rounded-lg border transition-colors ${rowPad} ${
            isSelected
              ? 'border-primary/40 bg-primary/10'
              : 'border-transparent hover:bg-muted'
          } ${isArchived ? 'opacity-70' : ''} cursor-pointer`}
        >
          {isRunning && !renaming && (
            <span
              className="relative flex h-2 w-2 flex-shrink-0"
              role="status"
              aria-label="Meridian is still working on this chat"
            >
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
            </span>
          )}

          {isUnread && !renaming && (
            <span
              className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary"
              role="status"
              aria-label="New reply in this chat"
            />
          )}

          {starred && !renaming && (
            <Star className="h-3.5 w-3.5 flex-shrink-0 fill-primary text-primary" aria-hidden />
          )}

          {renaming ? (
            <input
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitRename();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelRename();
                }
              }}
              aria-label="Rename chat"
              className={`min-w-0 flex-1 rounded-md border border-primary/50 bg-background px-1.5 py-0.5 ${textSize} text-foreground focus:outline-none focus:ring-1 focus:ring-primary`}
            />
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={`min-w-0 flex-1 truncate font-medium ${textSize} ${
                    isSelected ? 'text-foreground' : 'text-foreground group-hover:text-foreground'
                  }`}
                >
                  {truncateTitle(chat.title, compactMode ? 28 : 40)}
                </span>
              </TooltipTrigger>
              <TooltipContent side="right" align="start" className="max-w-xs">
                <p className="font-medium">{chat.title}</p>
                <p className="mt-0.5 text-xs opacity-80">
                  {chat.messageCount} message{chat.messageCount === 1 ? '' : 's'} · Created{' '}
                  {formatDate(chat.createdAt)} · Updated {formatDate(chat.lastMessageAt)}
                </p>
              </TooltipContent>
            </Tooltip>
          )}

          {!renaming && (
            <div
              className={`flex-shrink-0 transition-opacity ${
                menuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'
              }`}
            >
              <ChatItemMenu
                open={menuOpen}
                onOpenChange={setMenuOpen}
                isStarred={starred}
                isArchived={isArchived}
                projects={org.projects}
                currentProjectId={currentProjectId}
                onRename={startRename}
                onToggleStar={() => org.toggleStar(chat.id)}
                onDelete={onDeleteChat ? () => onDeleteChat(chat.id) : undefined}
                onRestore={isArchived ? () => onRestoreChat(chat.id) : undefined}
                onShowDetails={() => setDetailsOpen(true)}
                onAssignProject={(projectId) => org.assignChat(chat.id, projectId)}
                triggerClassName="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              />
            </div>
          )}
        </div>

        <ChatDetailsModal
          chat={chat}
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
          isStarred={starred}
          projectName={projectName}
        />
      </>
    );
  }
);

ChatItem.displayName = 'ChatItem';
